import { useEffect, useRef, useState } from 'react';
import API from '@/lib/api';
import { toast } from 'sonner';
import { scanArticle } from '@/lib/scanQuantity';
import { applyScanCustomerPrice, canPriceScanItem, scanPriceVariant } from '@/lib/scanCustomerPricing';

export const useScanCustomerPricing = ({ klantId, klanten, setKlanten, setKlantId, itemsRef, setItems, blocked }) => {
  const customer = klanten.find(k => k.id === klantId) || null;
  const customerRef = useRef(customer);
  customerRef.current = customer;
  const [request, setRequest] = useState(null), [busy, setBusy] = useState(false), [error, setError] = useState('');
  const lock = useRef(false), controller = useRef(null);
  useEffect(() => () => controller.current?.abort(), []);
  const ready = (!klantId || !!customer) && !request && !busy;
  const readyRef = useRef(ready); readyRef.current = ready;

  const commit = (nextId, nextCustomer) => {
    customerRef.current = nextCustomer;
    setKlantId(nextId);
  };
  const choose = async (nextId, force = false) => {
    if (blocked || lock.current) { toast.info('Rond eerst de huidige scan of bewerking af.'); return; }
    if (nextId === klantId && !force) return;
    const nextCustomer = klanten.find(k => k.id === nextId) || null;
    if (nextId && !nextCustomer) { toast.error('Klant is niet beschikbaar.'); return; }
    if (nextCustomer?.geblokkeerd) { toast.error('Deze klant is geblokkeerd.'); return; }
    setError('');
    if (itemsRef.current.some(canPriceScanItem)) {
      readyRef.current = false;
      setRequest({ id: nextId, customer: nextCustomer });
    } else if (!nextId) commit('', null);
    else {
      lock.current = true; readyRef.current = false; setBusy(true);
      const abort = new AbortController(); controller.current = abort;
      try {
        const fresh = (await API.get('/klanten', { noCache: true, timeout: 30000, signal: abort.signal })).data;
        const selected = fresh.find(k => k.id === nextId);
        if (!selected || selected.geblokkeerd) throw new Error('Klant is niet meer beschikbaar.');
        if (!abort.signal.aborted) { setKlanten(fresh); commit(nextId, selected); }
      } catch { if (!abort.signal.aborted) toast.error('Klantgegevens konden niet worden geladen. De klant is niet gewijzigd.'); }
      finally { lock.current = false; setBusy(false); }
    }
  };
  const confirm = async () => {
    if (!request || lock.current) return;
    lock.current = true; setBusy(true); setError('');
    const abort = new AbortController(); controller.current = abort;
    try {
      const currentCustomers = request.id ? (await API.get('/klanten', { noCache: true, timeout: 30000, signal: abort.signal })).data : klanten;
      const nextCustomer = request.id ? currentCustomers.find(k => k.id === request.id) : null;
      if (request.id && (!nextCustomer || nextCustomer.geblokkeerd)) throw new Error('Klant is niet meer beschikbaar.');
      const ids = [...new Set(itemsRef.current.filter(canPriceScanItem).map(item => item.art_id))];
      const articles = new Map(await Promise.all(ids.map(async id => {
        const response = await API.get(`/artikels/${encodeURIComponent(id)}`, { noCache: true, timeout: 30000, signal: abort.signal });
        return [id, scanArticle(response.data)];
      })));
      if (abort.signal.aborted) return;
      // Calculate everything first: a missing article/variant never partly reprices the cart.
      const updated = itemsRef.current.map(item => {
        if (!canPriceScanItem(item)) return item;
        const art = articles.get(item.art_id);
        return applyScanCustomerPrice(item, art, scanPriceVariant(art, item), nextCustomer);
      });
      setKlanten(currentCustomers); setItems(updated); commit(request.id, nextCustomer); setRequest(null);
      toast.success('Klantprijzen en kortingen toegepast.');
    } catch (err) {
      if (!abort.signal.aborted) setError(err.response?.data?.detail || 'Klantprijzen konden niet worden toegepast. De scanlijst is ongewijzigd.');
    } finally { lock.current = false; setBusy(false); }
  };
  const cancel = () => { if (!lock.current) { setRequest(null); setError(''); } };
  return { customer, customerRef, ready, readyRef, request, busy, error, choose, confirm, cancel };
};